/**
 * A query-report-header component defines the saving
 * and loading mechanisms for interacting with reports
 */
import {AttrsSchema, Component} from 'panel';

import {userDetailsStore} from 'common/report/stores/user-details';
import {featuresStore} from 'common/report/stores/features';
import {OverrideModal} from 'common/report/util/saved-entities/types';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {SearchSortOption} from 'common/report/util/search/types';
import {ContentDiscoveryType, SearchSource, SearchTrackedProperty} from 'common/report/util/search/tracking';
import {EntityType, EntityTypePermission} from 'common/report/util/saved-entities/types';
import {
  buildCreateEntityModalAttrs,
  getEntityType,
  getEntityTypeName,
  getSharableEntityType,
  getShareCapability,
  promptSuperadminOverrideSave,
  promptSuperadminOverrideShare,
} from 'common/report/util/saved-entities';
import {emitTrackEvent, propagateEvent} from 'common/util/dom';
import {serializeAttributes} from 'common/util';
import {pluralize} from 'common/util/string';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {promptSharing} from 'common/widgets/sharing';
import {FeatureGate} from 'common/report/constants';
import {IconSizeType} from 'common/constants';
import {useStore} from 'common/util/component-helpers';

import {QueryReportHeaderAttrs, queryReportHeaderAttrsSchema} from './types';
import {TrackingEvent} from './tracking';
import template from './index.jade';
import css from './index.styl?inline';

enum SaveModes {
  SaveNew = `save-new`,
  Edit = `edit`,
  Save = `save`,
}

export enum HeaderAction {
  Delete = `delete`,
  Edit = `edit`,
  Refresh = `refresh`,
  Save = `save`,
  SaveNew = `save-new`,
  // TODO: rename
  Load = `open`,
  Reset = `new`,
}

const RESET_ACTION = Object.freeze({
  icon: `new-report`,
  value: HeaderAction.Reset,
  label: `New`,
  elref: `reportNewButton`,
});

const EDIT_ACTION = Object.freeze({
  icon: `edit`,
  value: HeaderAction.Edit,
  label: `Edit Report`,
  elref: `reportOverflowEditButton`,
});

const REFRESH_ACTION = Object.freeze({
  icon: `refresh`,
  value: HeaderAction.Refresh,
  label: `Refresh Data`,
  elref: `reportRefreshButton`,
});

// TODO right now the deletion logic is on each report, when explore combines reports and cohorts without
// having separate logic to delete separately we can move bookmark deletion logic back to this component
// rather than separating it all out
const DELETE_ACTION = Object.freeze({
  icon: `trashcan`,
  value: HeaderAction.Delete,
  label: `Delete Report`,
  elref: `reportDeleteButton`,
});

interface QueryReportHeaderState {
  isBookmarkSelectorOpen: boolean;
  isDescriptionTruncated: boolean;
  saveMode: SaveModes;
  saveModalOpen: boolean;
  editWarningModalOpen: boolean;
  bookmarks: any[];
  openShareUponSave: boolean;
  showSaveBeforeShare: boolean;
}

export class QueryReportHeader extends Component<QueryReportHeaderState> {
  static get attrsSchema(): AttrsSchema<QueryReportHeaderAttrs> {
    return {...super.attrsSchema, ...queryReportHeaderAttrsSchema};
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      defaultState: {
        isBookmarkSelectorOpen: false,
        isDescriptionTruncated: true,
        saveMode: null,
        saveModalOpen: false,
        editWarningModalOpen: false,
        bookmarks: null,
        openShareUponSave: false,
        // Represents the state where we need to show Save modal before Share modal
        showSaveBeforeShare: false,
      },

      helpers: {
        IconSizeType,
        serializeAttributes,
        propagateEvent: (ev) => propagateEvent(this, ev),

        userIsEntityOwner: (entity): boolean => this.attr(`user-id`) === entity.userId,
        userIsStaff: (): boolean => userDetailsStore.value.isStaff,

        hasSelectedEntity: () => this.hasSelectedEntity,
        hasSlot: (name) => this.hasSlot(name),
        closeBookmarkSelector: () => this.changeBookmarkSelector(false),
        getHeaderActionOverflowItems: () => this.getHeaderActionOverflowItems(),
        getHeaderActionItems: () => this.getHeaderActionItems(),
        getHeaderActionItemsSlots: () => this.getHeaderActionItemsSlots(),
        getShareCapability: () => {
          // query-report-header gets entity object destructured through attrs
          const entity = {
            canShare: this.attr(`can-share`),
            canUpdateBasic: this.attr(`can-save`),
            allowStaffOverride: this.attr(`allow-staff-override`),
            isSuperadmin: this.attr(`is-superadmin`),
          };
          return getShareCapability(entity, EntityType.Report, {
            isSharingEnabled: this.helpers.isSharingEnabled(),
          });
        },
        allowSuperAdminOverride: () =>
          /** Allow super admin override for an entity that is valid but not save-able (due to permission) and user is a superadmin */
          (!this.attr(`can-save`) && !this.attr(`invalid-entity`) && this.attr(`is-superadmin`)) ||
          this.attr(`allow-staff-override`),
        handleSharingStatusButtonClick: async () => {
          // Open save modal when share is clicked in brand new report - only for share enabled projects
          if (this.helpers.shouldOpenSaveModalOnShare() && this.helpers.isSharingEnabled()) {
            this.update({
              openShareUponSave: true,
              showSaveBeforeShare: true,
            });
            this.openSaveModal(SaveModes.Save);
          } else {
            // Override Share
            let allowShare = true;
            if (this.helpers.allowSuperAdminOverride()) {
              allowShare = await promptSuperadminOverrideShare(getEntityType(this.helpers.getSharableEntityType()));
            }
            if (allowShare) {
              await promptSharing({
                type: this.helpers.getSharableEntityType(),
                id: this.attr(`selected-bookmark-id`),
                'project-id': this.attr(`project-id`),
              });
            }
          }
        },
        shouldOpenSaveModalOnShare: (): boolean => !this.attr(`selected-bookmark-id`) && !this.hasSelectedEntity,

        bookmarkSelected: (ev) => {
          const {value: report} = ev.detail.entity;
          this.dispatchEvent(new CustomEvent(`bookmarkselect`, {detail: {report}}));
        },

        // Set document title to report title
        updateDocumentTitle: (reportTitle = this.attr(`selected-bookmark-name`)) => {
          const projectName = this.project?.name;

          let newTitle;
          if (reportTitle && projectName) {
            newTitle = `${reportTitle} - ${projectName} / Mixpanel`;
          } else if (reportTitle) {
            newTitle = `${reportTitle} / Mixpanel`;
          } else if (projectName) {
            newTitle = `${projectName} / Mixpanel`;
          } else {
            newTitle = `Mixpanel`;
          }

          if (newTitle !== document.title) {
            document.title = newTitle;
          }
        },

        closeSaveModal: (ev) => this.closeSaveModal(ev),
        submitSaveModal: (ev) => {
          const type = ev.type;
          if (type === `saveAndShare`) {
            // When Share Management is clicked in Save Modal, we need to open Share modal
            this.update({
              openShareUponSave: true,
            });
          }
          this.submitSave(ev.detail.value);
        },

        /** Show save modal when editing is enabled */
        editReportClicked: () => {
          this.openSaveModal(this.hasSelectedEntity ? SaveModes.Edit : SaveModes.Save);
        },

        getSamplingNotice: () => {
          const samplingFactor = this.attr(`sampling-factor`);
          if (samplingFactor && samplingFactor < 1) {
            return `Sampled at ${samplingFactor * 100}%`;
          }

          return null;
        },

        onHeaderActionClick: async (ev) => {
          switch (ev.detail.value) {
            case HeaderAction.Edit:
              this.openSaveModal(SaveModes.Edit);
              break;
            case HeaderAction.Load:
              this.changeBookmarkSelector(true);
              break;
            case HeaderAction.SaveNew:
              if (!this.hasSelectedEntity) {
                // if no bookmark is currently selected, show bookmark edit modal
                this.openSaveModal(SaveModes.Save);
              } else {
                this.openSaveModal(SaveModes.SaveNew);
              }
              break;
            case HeaderAction.Save:
              if (!this.hasSelectedEntity) {
                // if no bookmark is currently selected, show bookmark edit modal
                this.openSaveModal(SaveModes.Save);
              } else if (this.attr(`num-dashboards`) || this.attr(`num-custom-alerts`)) {
                // if current bookmark IS part of dashboards or alerts, warn user via dialog
                this.update({editWarningModalOpen: true});
              } else if (this.helpers.allowSuperAdminOverride()) {
                const modalOutcome = await promptSuperadminOverrideSave(
                  getEntityType(this.helpers.getSharableEntityType()),
                );
                if (modalOutcome === OverrideModal.Copy) {
                  this.openSaveModal(SaveModes.SaveNew);
                } else if (modalOutcome !== OverrideModal.Closed && this.hasUnsavedChanges) {
                  this.submitSave(); // save directly
                }
              } else if (this.hasUnsavedChanges) {
                this.submitSave(); // save directly without any warning dialog
              }
              break;
            case HeaderAction.Reset:
              this.resetReport();
              break;
            default:
              this.dispatchEvent(new CustomEvent(`actionclick`, {detail: ev.detail}));
              break;
          }
        },

        editWarningModalSubmit: (ev) => {
          if (ev.detail.action === `confirm` && this.hasUnsavedChanges) {
            this.submitSave();
          }
          this.update({editWarningModalOpen: false});
        },

        toggleDescriptionTruncation: () => {
          // This check allows the user to select text within the description
          // WITHOUT toggling truncation. Only toggle if there is no selection.
          if (!window.getSelection().toString()) {
            this.update({
              isDescriptionTruncated: !this.state.isDescriptionTruncated,
            });
          }
        },

        getEditWarning: () => {
          const numDashboards = this.attr(`num-dashboards`);
          const numDashboardsText = numDashboards
            ? `${numDashboards === 1 ? `a` : numDashboards} ${pluralize(`Dashboard`, numDashboards)}`
            : ``;
          const numAlerts = this.attr(`num-custom-alerts`);
          const numAlertsText = numAlerts
            ? `${numAlerts === 1 ? `an` : numAlerts} ${pluralize(`Alert`, numAlerts)}`
            : ``;
          const conjunction = numAlerts && numDashboards ? ` and ` : ``;

          return [
            `This report is currently active on`,
            ` ${numAlertsText}${conjunction}${numDashboardsText}.`,
            ` By continuing, you’ll be updating the data across all instances.`,
          ].join(``);
        },

        buildCreateEntityModalAttrs: () => {
          const isDuplicating = this.state.saveMode === SaveModes.SaveNew;
          const isEditing = this.hasSelectedEntity && !isDuplicating;
          const showEditWarning = !!(this.attr(`num-dashboards`) || this.attr(`num-custom-alerts`));

          return buildCreateEntityModalAttrs({
            modalTitleOverride: this.state.showSaveBeforeShare ? `Save Before Sharing` : null,
            modalOpen: this.state.saveModalOpen,
            isEditing,
            isDuplicating,
            projectId: this.attr(`project-id`),
            entityType: this.attr(`entity-type`),
            // showSaveBeforeShare represents if a Save modal was triggered by the Share button.
            // We show Share Management button in the Save modal only when Save modal was triggered by Save button
            showShareManagement: this.helpers.isSharingEnabled() && !this.state.showSaveBeforeShare,
            entity: {
              name: this.attr(`selected-bookmark-name`),
              description: this.attr(`selected-bookmark-description`),

              // permissions
              can_view: this.attr(`is-superadmin`) || this.attr(`can-save`),
              can_update_basic: this.attr(`can-save`),
              can_share: this.attr(`can-share`),
              is_superadmin: this.attr(`is-superadmin`),
              allow_staff_override: this.attr(`allow-staff-override`),
            },
            editWarning: showEditWarning && this.helpers.getEditWarning(),
          });
        },
        canUserShare: () => this.helpers.getShareCapability().enableShare,
        isShareButtonEnabled: () => {
          const selectedBookmarkId = this.attr(`selected-bookmark-id`);
          // When a user loads a report and makes changes, the URL loses bookmark-id
          // in case a user either refreshes the page OR share the modified URL
          // we lose bookmark-id but app thinks we have selected a report
          // in this case we should disable share button as all share APIs will fail w/o bookmark-id
          if (this.hasSelectedEntity && !selectedBookmarkId) {
            return false;
          } else if (this.attr(`invalid-entity`)) {
            return false;
          } else if (!this.hasSelectedEntity && !selectedBookmarkId && !this.attr(`invalid-entity`)) {
            // a consumer should not see enabled share button on a brand new report
            return !this.isUserConsumer();
          } else if (!this.helpers.canUserShare()) {
            return false;
          } else {
            return true;
          }
        },

        isSharingEnabled: () => {
          return featuresStore.value?.getFeatureGate(FeatureGate.Sharing);
        },

        getSharableEntityType: () => getSharableEntityType(this.attr(`entity-type`)),

        getShareTooltipText: () => {
          const shareActionItem = this.getShareButtonAction();

          return shareActionItem.tooltip?.text ?? ``;
        },
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();

    // update header when user details are updated (from backend or otherwise)
    const userStoreCallback = () => this.helpers.updateDocumentTitle();
    useStore(this, userDetailsStore, userStoreCallback).fetch();
    useStore(this, featuresStore).fetch({projectId: this.attr(`project-id`)});
  }

  public disconnectedCallback() {
    // Reset document title as page navigates away
    this.helpers.updateDocumentTitle(``);
    super.disconnectedCallback();
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (attr === `selected-bookmark-name`) {
      this.helpers.updateDocumentTitle();
    }

    const mpSharingModalElement = document.querySelector(`mp-sharing-modal`);
    if (attr === `is-save-failed` && this.attr(`is-save-failed`) && mpSharingModalElement) {
      mpSharingModalElement.setAttribute(`open`, false);
    }
    // In case where a user saves a new report or saves a report as a duplicate
    if (attr === `selected-bookmark-id` && (oldVal === null || oldVal !== newVal)) {
      const newBookmarkId = this.attr(`selected-bookmark-id`);
      if (newBookmarkId && this.state.openShareUponSave) {
        promptSharing({
          type: this.helpers.getSharableEntityType(),
          id: newBookmarkId,
          'project-id': this.attr(`project-id`),
        });
        this.update({
          openShareUponSave: false,
        });
      }
    }
  }

  set bookmarks(bookmarks) {
    this.update({bookmarks});
  }

  get hasSelectedEntity() {
    return !!(this.attr(`selected-bookmark-id`) || this.attr(`selected-bookmark-name`));
  }

  get hasUnsavedChanges() {
    // if there is no entity selected/saved, then by definition there are unsaved changes
    return !this.hasSelectedEntity || this.attr(`has-unsaved-changes`);
  }

  get project() {
    return userDetailsStore.value.projects[this.attr(`project-id`)] || null;
  }

  /**
   * Checks for presence of a slotted child element
   */
  private hasSlot(slotName: string): boolean {
    return !!this.querySelector(`[slot="${slotName}"]`);
  }

  private changeBookmarkSelector(open: boolean) {
    const event = `bookmarkselector${open ? `open` : `close`}`;

    this.update({isBookmarkSelectorOpen: open});
    this.dispatchEvent(new CustomEvent(event));

    if (open) {
      this.track(TrackingEvent.ReportListOpen, {
        [SearchTrackedProperty.ContentDiscoveryType]: ContentDiscoveryType.Browse,
        [SearchTrackedProperty.SortValue]: SearchSortOption.Default,
        [SearchTrackedProperty.Source]: SearchSource.Modal,
      });
    }
  }

  private openSaveModal(saveMode: SaveModes) {
    this.update({
      saveMode,
      saveModalOpen: true,
    });
  }

  private closeSaveModal(ev) {
    // When Cancel button is clicked inside the Save modal triggered by the Share button
    // we need to clear openShareUponSave state in order to prevent the Share modal
    // showing up in subsequent Save Modal unexpectedly
    const clearOpenShareUponSave = ev.detail.clearOpenShareUponSave;
    this.update({
      saveMode: null,
      saveModalOpen: false,
      showSaveBeforeShare: false,
      openShareUponSave: clearOpenShareUponSave ? false : this.state.openShareUponSave,
    });
  }

  private submitSave(newReportData = null) {
    const saveAsNew = this.state.saveMode === SaveModes.SaveNew || !this.hasSelectedEntity;

    const reportData = {
      name: this.attr(`selected-bookmark-name`),
      description: this.attr(`selected-bookmark-description`),
      saveAsNew,
      ...(newReportData || {}),
    };

    this.dispatchEvent(new CustomEvent(`save`, {detail: reportData}));

    this.update({
      saveMode: null,
      saveModalOpen: false,
    });

    this.track(saveAsNew ? TrackingEvent.ReportSavedAsNew : TrackingEvent.ReportSaved, {
      'is owner': saveAsNew || this.attr(`user-id`) === this.attr(`selected-bookmark-user-id`),
      name: this.attr(`selected-bookmark-name`),
      description: this.attr(`selected-bookmark-description`),
    });

    // This allows opening up the Share modal as soon as Save button is clicked, granted it will just show a spinner.
    // Once actual save completes via API, another promptSharing will trigger (in attributeChangedCallback) w/
    // the correct bookmark-id
    if (this.state.openShareUponSave) {
      promptSharing({
        type: this.helpers.getSharableEntityType(),
        id: this.attr(`selected-bookmark-id`),
        'project-id': this.attr(`project-id`),
      });
    }
  }

  private resetReport() {
    this.track(TrackingEvent.ResetState);
    this.dispatchEvent(new CustomEvent(`reset`));
  }

  private getHeaderActionItemsSlots() {
    const controlItems = this.attr(`control-items`);
    return controlItems.filter((item) => Object.prototype.hasOwnProperty.call(item, `slot`));
  }

  private getHeaderActionOverflowItems(): Section[] {
    const items = this.attr(`overflow-control-items`);
    return [...this.getDefaultHeaderItems(), ...items];
  }

  private getDefaultHeaderItems(): Section[] {
    const canEdit = this.hasSelectedEntity && this.attr(`can-write`) && this.attr(`can-save`);
    const canDelete = this.hasSelectedEntity && this.attr(`can-write`);
    const secondSectionItems = {
      items: [...(canEdit ? [EDIT_ACTION] : []), ...(canDelete ? [DELETE_ACTION] : [])],
    };
    const defaultItems = [{items: [REFRESH_ACTION]}];
    if (canEdit || canDelete) {
      defaultItems.push(secondSectionItems);
    }
    return defaultItems;
  }

  private isUserConsumer(): boolean {
    const userIsConsumer = !userDetailsStore.value.projects[this.attr(`project-id`)]?.permissions.includes(
      EntityTypePermission.Visibility,
    );
    return userIsConsumer && !userDetailsStore.value.isStaff;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private getHeaderActionItems(): Section {
    const actions: SectionItem[] = [];
    const entityTypeName = getEntityTypeName(this.attr(`entity-type`));
    const canSave = !this.attr(`invalid-entity`) && (this.attr(`can-save`) || this.helpers.allowSuperAdminOverride());
    const isSaveButtonDisabled = !canSave || !this.hasUnsavedChanges;

    actions.push({
      slot: `createdBy`,
      label: null,
    });

    // Share button
    const shareActionItem = this.getShareButtonAction();
    // When `is-select` mode is on for the share button, remove tooltip
    // section to avoid double tooltip for the share button and the
    // menu item in mp-select
    delete shareActionItem.tooltip;
    actions.push(shareActionItem);

    let saveTooltip = null;

    if (this.attr(`can-write`)) {
      if (isSaveButtonDisabled) {
        if (this.attr(`invalid-entity`)) {
          saveTooltip = this.attr(`invalid-message`) || `Current ${entityTypeName} is invalid`;
        } else if (this.hasSelectedEntity && !canSave) {
          saveTooltip = this.helpers.isSharingEnabled()
            ? `You have view-only access and cannot save changes. Save as new to create a copy.`
            : `Editing is restricted on this report. Save as new to create a copy.`;
        } else if (!this.hasUnsavedChanges) {
          saveTooltip = `No unsaved changes`;
        }
      }

      actions.push(RESET_ACTION);

      if (!this.attr(`hide-save-buttons`)) {
        actions.push(
          {
            icon: `save`,
            label: `Save`,
            tooltip: saveTooltip ? {text: saveTooltip} : null,
            value: HeaderAction.Save,
            elref: `reportSaveButton`,
            isDisabled: isSaveButtonDisabled,
          },
          {
            icon: `duplicate`,
            label: `Save as New`,
            tooltip: this.attr(`invalid-entity`) ? {text: saveTooltip} : null,
            value: HeaderAction.SaveNew,
            elref: `reportSaveNewButton`,
            isDisabled: this.attr(`invalid-entity`),
          },
        );
      }
    }

    if (!this.attr(`hide-load-control`)) {
      // Load button
      actions.push({
        value: HeaderAction.Load,
        label: `Open`,
        icon: `open`,
        elref: `reportLoadButton`,
      });
    }

    return {
      items: actions.concat(this.attr(`control-items`)),
    };
  }

  private getShareButtonAction() {
    let shareTooltip = null;
    if (!this.helpers.isShareButtonEnabled()) {
      if (this.attr(`invalid-entity`)) {
        shareTooltip = this.attr(`invalid-message`) || `Current report is invalid`;
      } else {
        const shareCapability = this.helpers.getShareCapability();
        shareTooltip = shareCapability.disabledShareReason;
        // this is a safeguard against buggy insights behavior. When a user loads a report, make changes, then refresh
        // or share the link, they will see `Cannot share due to consumer role` error message even though they are not
        // a consumer. This is due to insights not resetting data correctly on refresh. If there are any changes to a
        // loaded report - we can't not infer the entity from the URL. So upon refresh, insights lose all context
        // such as entity-id. But it keeps the entity name for some reason, making it look like we loaded a valid report
        // with changes. This if logic prevents the wrong message from showing up to prevent confusion. We detect this
        // odd state by seeing if id==0 and name='xyz' => I have selected bookmark name but no id!
        // TODO: make Insights app behave the same as the retention app
        if (
          shareTooltip &&
          !shareCapability.enableShare &&
          !this.attr(`selected-bookmark-id`) &&
          this.attr(`selected-bookmark-name`)
        ) {
          shareTooltip = `Save ${this.attr(`entity-type`)} to enable sharing`;
        }
      }
    }
    const shareAction = {
      slot: `sharing`,
      tooltip: shareTooltip ? {text: shareTooltip} : null,
      label: null,
    };

    return shareAction;
  }

  private track(event: string, props: Obj = {}) {
    if (this.attr(`track-name`)) {
      emitTrackEvent(this, {
        eventName: event,
        eventPrefix: `mp-query-report-header`,
        props: {
          ...props,
          project_id: this.attr(`project-id`),
          tab: this.attr(`track-name`),
          workspace_id: this.attr(`workspace-id`),
        },
        cancelable: true,
      });
    }
  }
}

export const QueryReportHeaderEl = defineAndMakeJsxFactory(`mp-query-report-header`, QueryReportHeader);
